import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  getProducts,
  getAdminOrders,
  getCoupons,
  createProduct,
  updateProduct as apiUpdateProduct,
  deleteProduct as apiDeleteProduct,
  createCoupon,
  deleteCoupon as apiDeleteCoupon,
  updateOrderStatus as apiUpdateOrderStatus
} from "@/lib/api";
import { toast } from "sonner";

const StoreContext = createContext(null);

const CART_KEY = "alaira.cart.v1";
const WISH_KEY = "alaira.wishlist.v1";
const NOTE_KEY = "alaira.note.v1";

const readLS = (k, fallback) => {
  try {
    const v = localStorage.getItem(k);
    return v ? JSON.parse(v) : fallback;
  } catch { return fallback; }
};

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => readLS(CART_KEY, []));
  const [wishlist, setWishlist] = useState(() => readLS(WISH_KEY, []));
  const [note, setNote] = useState(() => readLS(NOTE_KEY, ""));

  // Admin states
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem(WISH_KEY, JSON.stringify(wishlist)); }, [wishlist]);
  useEffect(() => { localStorage.setItem(NOTE_KEY, JSON.stringify(note)); }, [note]);

  // Load admin data
  const loadAdminData = async () => {
    try {
      const [prodsData, ordersData, couponsData] = await Promise.all([
        getProducts().catch(() => []),
        getAdminOrders().catch(() => []),
        getCoupons().catch(() => [])
      ]);

      // Normalize products to have _id field matching what the Next.js client admin panel expects
      const normalizedProducts = (prodsData || []).map(p => ({
        ...p,
        _id: p.id || p._id,
        originalPrice: p.original_price || p.price,
        images: Array.isArray(p.images) ? p.images : (p.image ? [p.image] : [])
      }));

      // Normalize orders to match Next.js shape
      const normalizedOrders = (ordersData || []).map(o => ({
        ...o,
        _id: o._id || o.id,
        customerInfo: {
          name: o.shippingAddress?.fullName || o.customer || "Guest",
          phone: o.shippingAddress?.phone || o.phone || ""
        }
      }));

      setProducts(normalizedProducts);
      setOrders(normalizedOrders);
      setCoupons(couponsData || []);

      // Extract unique customers from orders
      const customerMap = {};
      normalizedOrders.forEach(o => {
        const name = o.customerInfo?.name || "Guest";
        const phone = o.customerInfo?.phone || "";
        const key = name + "_" + phone;
        if (!customerMap[key]) {
          customerMap[key] = {
            _id: o.userId || key,
            name,
            phone,
            email: "customer@vastraaura.com", // Fallback email
            totalSpending: 0,
            totalOrders: 0,
            createdAt: o.createdAt || new Date().toISOString()
          };
        }
        customerMap[key].totalSpending += o.total || 0;
        customerMap[key].totalOrders += 1;
      });
      setCustomers(Object.values(customerMap));

      // Extract mock reviews
      const allReviews = [];
      normalizedProducts.forEach(p => {
        if (p.reviews && Array.isArray(p.reviews)) {
          p.reviews.forEach(r => {
            allReviews.push({
              _id: r._id || Math.random().toString(),
              productTitle: p.title || p.name,
              rating: r.rating || 5,
              comment: r.comment || "",
              author: r.author || "Anonymous",
              date: r.date || "Recently"
            });
          });
        }
      });
      setReviews(allReviews);

    } catch (err) {
      console.error("Failed to load admin data in StoreContext:", err);
    }
  };

  // Load once initially
  useEffect(() => {
    loadAdminData();
  }, []);

  // Admin Actions wrapping the backend APIs
  const addProduct = async (payload) => {
    try {
      const res = await createProduct(payload);
      toast.success("Product created successfully");
      await loadAdminData();
      return res;
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to create product");
      throw err;
    }
  };

  const updateProduct = async (id, payload) => {
    try {
      const res = await apiUpdateProduct(id, payload);
      toast.success("Product updated successfully");
      await loadAdminData();
      return res;
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update product");
      throw err;
    }
  };

  const deleteProduct = async (id) => {
    try {
      await apiDeleteProduct(id);
      toast.success("Product deleted successfully");
      await loadAdminData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete product");
      throw err;
    }
  };

  const addCoupon = async (payload) => {
    try {
      const res = await createCoupon(payload);
      toast.success("Coupon created successfully");
      await loadAdminData();
      return res;
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to create coupon");
      throw err;
    }
  };

  const deleteCoupon = async (id) => {
    try {
      await apiDeleteCoupon(id);
      toast.success("Coupon deleted successfully");
      await loadAdminData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete coupon");
      throw err;
    }
  };

  const updateOrderStatus = async (id, status) => {
    try {
      await apiUpdateOrderStatus(id, status);
      toast.success("Order status updated successfully");
      await loadAdminData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update order status");
      throw err;
    }
  };

  const addToCart = (product, qty = 1) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        return prev.map((i) => i.id === product.id ? { ...i, qty: i.qty + qty } : i);
      }
      return [...prev, {
        id: product.id, name: product.name, price: product.price, image: product.image, qty,
      }];
    });
  };

  const updateQty = (id, qty) => {
    if (qty <= 0) return removeFromCart(id);
    setCart((prev) => prev.map((i) => i.id === id ? { ...i, qty } : i));
  };
  const removeFromCart = (id) => setCart((prev) => prev.filter((i) => i.id !== id));
  const clearCart = () => setCart([]);

  const toggleWishlist = (product) => {
    setWishlist((prev) => {
      const exists = prev.find((i) => i.id === product.id);
      if (exists) return prev.filter((i) => i.id !== product.id);
      return [...prev, {
        id: product.id, name: product.name, price: product.price, image: product.image,
      }];
    });
  };
  const isWished = (id) => wishlist.some((i) => i.id === id);

  const totals = useMemo(() => {
    const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
    const shipping = subtotal === 0 ? 0 : subtotal >= 2500 ? 0 : 99;
    const total = subtotal + shipping;
    const itemCount = cart.reduce((s, i) => s + i.qty, 0);
    return { subtotal, shipping, total, itemCount };
  }, [cart]);

  const value = {
    cart, wishlist, note, setNote,
    addToCart, updateQty, removeFromCart, clearCart,
    toggleWishlist, isWished, totals,
    
    // Expose Admin elements
    products, coupons, orders, customers, reviews,
    addProduct, updateProduct, deleteProduct,
    addCoupon, deleteCoupon, updateOrderStatus,
    reloadAdminData: loadAdminData
  };
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used inside StoreProvider");
  return ctx;
};
